import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  FileText, 
  Printer,
  Sparkles,
  Loader2,
  Target,
  Search,
  AlertCircle,
  CheckCircle2,
  BarChart3,
  Users,
  Lightbulb
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const A3_SECTIONS = [
  { key: "background", label: "1. Background", icon: FileText, color: "border-l-blue-500", placeholder: "Context and reason for addressing this problem" },
  { key: "currentState", label: "2. Current State", icon: Search, color: "border-l-amber-500", placeholder: "What is happening now? Include data and observations" },
  { key: "targetState", label: "3. Target State", icon: Target, color: "border-l-emerald-500", placeholder: "What should the future state look like? Specific, measurable goals" },
  { key: "gapAnalysis", label: "4. Gap Analysis", icon: AlertCircle, color: "border-l-red-500", placeholder: "What is causing the gap between current and target?" },
  { key: "countermeasures", label: "5. Countermeasures", icon: Lightbulb, color: "border-l-violet-500", placeholder: "Proposed solutions to close the gap" },
  { key: "implementation", label: "6. Implementation Plan", icon: CheckCircle2, color: "border-l-cyan-500", placeholder: "Who, what, when - specific action steps" },
  { key: "metrics", label: "7. Metrics & Follow-up", icon: BarChart3, color: "border-l-pink-500", placeholder: "How will we measure success? Check-in dates" },
];

export const A3Template = ({ onAIAssist }) => {
  const [title, setTitle] = useState("");
  const [owner, setOwner] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [sections, setSections] = useState({
    background: "",
    currentState: "",
    targetState: "",
    gapAnalysis: "",
    countermeasures: "",
    implementation: "",
    metrics: "",
  });
  const [aiLoading, setAiLoading] = useState(false);

  const updateSection = (key, value) => {
    setSections(prev => ({ ...prev, [key]: value }));
  };

  const getAISuggestions = async () => {
    if (!title.trim()) {
      toast.error("Please enter a problem title first");
      return;
    }

    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/a3`, {
        problem: title.trim(),
        background: sections.background || null,
        current_state: sections.currentState || null,
        goal: sections.targetState || null,
        root_cause: sections.gapAnalysis || null,
        countermeasures: `${sections.countermeasures}\n\nImplementation: ${sections.implementation}\n\nMetrics: ${sections.metrics}` || null
      }, { withCredentials: true });

      if (onAIAssist) {
        onAIAssist(response.data.draft);
      }
      toast.success("AI review complete - check the chat");
    } catch (error) {
      toast.error("Failed to get AI review");
    } finally {
      setAiLoading(false);
    }
  };

  const handlePrint = () => window.print();

  return (
    <Card className="bento-card print:shadow-none print:border-gray-300">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            A3 Problem Solving
          </CardTitle>
          <div className="flex gap-2 no-print">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
            <Button size="sm" onClick={getAISuggestions} disabled={aiLoading}>
              {aiLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              AI Review
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Header Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-zinc-800/50 rounded-lg">
          <div>
            <Label className="text-xs text-muted-foreground">Problem Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Clear, specific problem statement"
              className="mt-1 font-medium"
              data-testid="a3-title"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="w-3 h-3" /> Owner
            </Label>
            <Input
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              placeholder="Who is responsible?"
              className="mt-1"
              data-testid="a3-owner"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1"
              data-testid="a3-date"
            />
          </div>
        </div>

        {/* A3 Sections - Left Side (Problem) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Problem Definition</p>
            {A3_SECTIONS.slice(0, 4).map((section) => (
              <div 
                key={section.key} 
                className={`p-3 rounded-lg bg-zinc-800/30 border-l-4 ${section.color}`}
              >
                <Label className="font-medium flex items-center gap-2">
                  <section.icon className="w-4 h-4" />
                  {section.label}
                </Label>
                <Textarea
                  value={sections[section.key]}
                  onChange={(e) => updateSection(section.key, e.target.value)}
                  placeholder={section.placeholder}
                  className="mt-2 bg-transparent text-sm"
                  rows={3}
                  data-testid={`a3-${section.key}`}
                />
              </div>
            ))}
          </div>

          {/* A3 Sections - Right Side (Solution) */}
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Solution & Action</p>
            {A3_SECTIONS.slice(4).map((section) => (
              <div 
                key={section.key} 
                className={`p-3 rounded-lg bg-zinc-800/30 border-l-4 ${section.color}`}
              >
                <Label className="font-medium flex items-center gap-2">
                  <section.icon className="w-4 h-4" />
                  {section.label}
                </Label>
                <Textarea
                  value={sections[section.key]}
                  onChange={(e) => updateSection(section.key, e.target.value)}
                  placeholder={section.placeholder}
                  className="mt-2 bg-transparent text-sm"
                  rows={3}
                  data-testid={`a3-${section.key}`}
                />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default A3Template;
